import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import { createStyles, makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { type ReactNode, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAssetDetailsContext } from "../../context/assetDetailsContext";
import { AssetAttachmentsGrid } from "./AssetAtachments/AssetAttachmentsGrid";
import { AssetLOTOGrid } from "./AssetLOTO/AssetLOTOGrid";
import { AssetPartsGrid } from "./AssetPartsGrid/AssetPartsGrid";
import { AssetTextGrid } from "./AssetText/AssetTextGrid";
import { Paper } from "@material-ui/core";
import { AssetQCBDTable } from "./AssetQCBD/AssetQCBD";
import { AssetDocumentsGrid } from "./AssetDocumentsGrid/AssetDocumentsGrid";

const useStyles = makeStyles((theme) =>
	createStyles({
		paper: {
			height: "100%",
			width: "100%",
			padding: 10,
		},
		customTabs: {
			minHeight: 35,
			"& .MuiTabs-indicator": {
				display: "none",
			},
			"& .MuiTabs-flexContainer": {
				borderBottom: "1px solid #e0e0e0",
			},
			"& .MuiTabs-scrollButtons": {
				color: theme.palette.primary.main,
				"&.Mui-disabled": {
					opacity: 0.3,
				},
			},
		},
		customTab: {
			textTransform: "none",
			fontWeight: "bold",
			fontSize: "12px",
			minHeight: 35,
			minWidth: "120px",
			padding: "6px 16px",
			color: "#666666",
			transition: "all 0.2s ease-in-out",
			"&:hover": {
				backgroundColor: `${theme.palette.primary.main}15`,
				color: theme.palette.primary.main,
			},
			"&.Mui-selected": {
				color: "#ffffff",
				backgroundColor: theme.palette.primary.main,
				borderRadius: "4px 4px 0 0",
				borderBottom: `3px solid ${theme.palette.primary.main}`,
				"&:hover": {
					backgroundColor: theme.palette.primary.main,
					color: "#ffffff",
				},
			},
		},
	}),
);

function a11yProps(index: number) {
	return {
		id: `asset-tab-${index}`,
		"aria-controls": `asset-tabpanel-${index}`,
	};
}

type TabPanelProps = {
	children?: ReactNode;
	index: number;
	value: number;
};

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
			style={{ height: "100%" }}
		>
			{value === index && (
				<Box sx={{}} style={{ height: "100%" }}>
					{children}
				</Box>
			)}
		</div>
	);
}

const RESOURCES: Resource[] = [
	{
		ResourceName: "Parts",
		Value: 0,
	},
	{
		ResourceName: "Documents",
		Value: 1,
	},
	{
		ResourceName: "Attachments",
		Value: 2,
	},
	{
		ResourceName: "LOTO",
		Value: 3,
	},
	{
		ResourceName: "Text",
		Value: 4,
	},
	{
		ResourceName: "QCBD",
		Value: 5,
	},
];

type Resource = {
	ResourceName: string;
	Value: number;
};

type Props = {
	searchStatistics: Function;
};

const clearClipboard = () => {
	localStorage.removeItem("attachmentCopied");
	localStorage.removeItem("documentCopied");
};

export default function AssetDetailsTabs({ searchStatistics }: Props) {
	const classes = useStyles();
	const theme = useTheme();
	const [searchParams, setSearchParams] = useSearchParams();
	const resource = searchParams.get("resource") ?? "";
	const isWideScreen = useMediaQuery("(min-width:1300px)");
	const {
		state: { gridValue },
		actions: { setGridValue, setCurrentGridName },
	} = useAssetDetailsContext();

	const handleChangeResource = (e: any, newValue: number) => {
		const resourceName: string = (e.target?.innerText).toLowerCase();
		clearClipboard();
		searchParams.set("resource", resourceName);
		setCurrentGridName(resourceName);
		setGridValue(newValue);
		setSearchParams(searchParams, { replace: true });
	};

	useEffect(() => {
		const resourceCurrentData = RESOURCES.find(
			(r) => r.ResourceName.toLocaleLowerCase() == resource,
		);
		setGridValue(resourceCurrentData?.Value);
		setCurrentGridName(resource);
		clearClipboard();
	}, [resource]);

	return (
		<Grid container>
			<Paper elevation={1} className={classes.paper}>
				<Grid item md={12} xs={12} style={{ height: "auto" }}>
					<Box
						id="tabs-grid"
						sx={{
							borderBottom: 1,
							borderColor: "divider",
						}}
					>
						<Tabs
							value={gridValue}
							onChange={(e, newValue: number) =>
								handleChangeResource(e, newValue)
							}
							variant={isWideScreen ? "fullWidth" : "scrollable"}
							{...(isWideScreen ? {} : { scrollButtons: "auto" })}
							aria-label="asset tabs"
							className={classes.customTabs}
						>
							{RESOURCES.map((resource) => (
								<Tab
									key={resource.Value}
									label={resource.ResourceName}
									{...a11yProps(resource.Value)}
									className={classes.customTab}
								/>
							))}
						</Tabs>
					</Box>
				</Grid>
				<Grid item md={12} xs={12} style={{ height: "88%" }}>
					<TabPanel value={gridValue} index={0}>
						<AssetPartsGrid searchStatistics={searchStatistics} />
					</TabPanel>
					<TabPanel value={gridValue} index={1}>
						<AssetDocumentsGrid searchStatistics={searchStatistics} />
					</TabPanel>
					<TabPanel value={gridValue} index={2}>
						<AssetAttachmentsGrid searchStatistics={searchStatistics} />
					</TabPanel>
					<TabPanel value={gridValue} index={3}>
						<AssetLOTOGrid searchStatistics={searchStatistics} />
					</TabPanel>
					<TabPanel value={gridValue} index={4}>
						<AssetTextGrid />
					</TabPanel>
					<TabPanel value={gridValue} index={5}>
						<AssetQCBDTable />
					</TabPanel>
				</Grid>
			</Paper>
		</Grid>
	);
}
